import { useEffect, useState } from 'react';
import { getAppViewport } from '../utils/appViewport';

// These are the app's fixed controls that can cover feed content. Intersection
// Observer accounts for scrolling/clipping, but not for controls painted over it.
const CONTROL_SELECTOR = '.footerNoBlur, .footer, .playPreviewsFooter, .trackBottomControls, .sectionsControlContainer, .homePinnedAskVisible, .header, .savedBrowsingHeader, .savedBrowsingControlsViewport, .savedSelectionBar';
const subscribers = new Map();
let observer;
let resizeObserver;
let mutationObserver;
let frame;
let pageHidden = false;
let observedLayouts = new Set();

const hasVisibleLayout = element => {
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && style.visibility !== 'collapse'
        && !element.closest('[hidden], [inert], [aria-hidden="true"]');
};

const overlaps = (first, second) => first.left < second.right && first.right > second.left
    && first.top < second.bottom && first.bottom > second.top;

const fitsClippingAncestors = (element, strip) => {
    for (let ancestor = element.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
        const style = window.getComputedStyle(ancestor);
        const clipsX = /^(auto|scroll|hidden|clip)$/.test(style.overflowX || style.overflow);
        const clipsY = /^(auto|scroll|hidden|clip)$/.test(style.overflowY || style.overflow);
        if (!clipsX && !clipsY) continue;
        const bounds = ancestor.getBoundingClientRect();
        if (clipsX && (strip.left < bounds.left || strip.right > bounds.right)) return false;
        if (clipsY && (strip.top < bounds.top || strip.bottom > bounds.bottom)) return false;
    }
    return true;
};

export const bottomStripFitsViewport = (strip, viewport, controls = []) => strip.width > 0 && strip.height > 0
    && strip.top >= viewport.top && strip.bottom <= viewport.bottom
    && strip.left >= viewport.left && strip.right <= viewport.right
    && !controls.some(control => overlaps(strip, control));

const measureVisibility = () => {
    frame = undefined;
    const visualViewport = window.visualViewport;
    const viewport = {
        top: visualViewport?.offsetTop || 0,
        left: visualViewport?.offsetLeft || 0,
    };
    viewport.bottom = viewport.top + (visualViewport?.height || window.innerHeight);
    viewport.right = viewport.left + (visualViewport?.width || window.innerWidth);
    const appViewport = getAppViewport();
    viewport.top = Math.max(viewport.top, appViewport.top);
    viewport.left = Math.max(viewport.left, appViewport.left);
    viewport.bottom = Math.min(viewport.bottom, appViewport.bottom);
    viewport.right = Math.min(viewport.right, appViewport.right);

    const controls = Array.from(document.querySelectorAll(CONTROL_SELECTOR));
    const modals = Array.from(document.querySelectorAll('.MuiModal-root')).filter(element => {
        const bounds = element.getBoundingClientRect();
        return hasVisibleLayout(element) && bounds.width > 0 && bounds.height > 0;
    });
    const topModal = modals[modals.length - 1];
    const controlRects = controls.filter(element => {
        const position = window.getComputedStyle(element).position;
        return (position === 'fixed' || position === 'sticky') && hasVisibleLayout(element);
    }).map(element => ({ element, rect: element.getBoundingClientRect() }))
        .filter(control => control.rect.width > 0 && control.rect.height > 0);

    // Reuse one set of layout listeners for every card, including controls that
    // appear or change size after the feed has already been displayed.
    const cardLayouts = Array.from(subscribers.keys(), element => element.parentElement).filter(Boolean);
    const viewportElement = document.getElementById('appViewport');
    const layouts = new Set([...controls, ...modals, ...cardLayouts, viewportElement].filter(Boolean));
    if (resizeObserver) {
        observedLayouts.forEach(element => { if (!layouts.has(element)) resizeObserver.unobserve(element); });
        layouts.forEach(element => { if (!observedLayouts.has(element)) resizeObserver.observe(element); });
        observedLayouts = layouts;
    }

    const updateVisibility = (subscription, visible) => {
        if (subscription.visible === visible) return;
        subscription.visible = visible;
        subscription.setVisible(visible);
    };
    const documentVisible = !pageHidden && document.visibilityState !== 'hidden';
    subscribers.forEach((subscription, element) => {
        if (!documentVisible || !subscription.intersecting) {
            updateVisibility(subscription, false);
            return;
        }
        const modal = element.closest('.MuiModal-root');
        const active = element.isConnected && hasVisibleLayout(element.parentElement)
            && (!topModal || topModal.contains(element));
        const coveredBy = controlRects.filter(control => !control.element.contains(element)
            && (!modal || modal.contains(control.element))).map(control => control.rect);
        const strip = element.getBoundingClientRect();
        updateVisibility(subscription, active
            && bottomStripFitsViewport(strip, viewport, coveredBy) && fitsClippingAncestors(element, strip));
    });
};

const scheduleMeasurement = () => {
    if (frame === undefined) frame = window.requestAnimationFrame(measureVisibility);
};
const hidePage = () => { pageHidden = true; scheduleMeasurement(); };
const showPage = () => { pageHidden = false; scheduleMeasurement(); };

const startObserving = () => {
    pageHidden = false;
    if (typeof IntersectionObserver === 'function') {
        observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const subscription = subscribers.get(entry.target);
                if (subscription) subscription.intersecting = entry.isIntersecting && entry.intersectionRatio === 1;
            });
            scheduleMeasurement();
        }, { threshold: 1 });
    }
    if (typeof ResizeObserver === 'function') resizeObserver = new ResizeObserver(scheduleMeasurement);
    if (typeof MutationObserver === 'function') {
        mutationObserver = new MutationObserver(scheduleMeasurement);
        mutationObserver.observe(document.body, {
            subtree: true, childList: true, attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'inert', 'aria-hidden', 'data-ask-open'],
        });
    }
    document.addEventListener('scroll', scheduleMeasurement, true);
    document.addEventListener('visibilitychange', scheduleMeasurement);
    window.addEventListener('resize', scheduleMeasurement);
    window.addEventListener('pagehide', hidePage);
    window.addEventListener('pageshow', showPage);
    window.visualViewport?.addEventListener('resize', scheduleMeasurement);
    window.visualViewport?.addEventListener('scroll', scheduleMeasurement);
};

const stopObserving = () => {
    observer?.disconnect();
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
    observer = resizeObserver = mutationObserver = undefined;
    observedLayouts.clear();
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    frame = undefined;
    document.removeEventListener('scroll', scheduleMeasurement, true);
    document.removeEventListener('visibilitychange', scheduleMeasurement);
    window.removeEventListener('resize', scheduleMeasurement);
    window.removeEventListener('pagehide', hidePage);
    window.removeEventListener('pageshow', showPage);
    window.visualViewport?.removeEventListener('resize', scheduleMeasurement);
    window.visualViewport?.removeEventListener('scroll', scheduleMeasurement);
};

export default function usePreviewBottomVisible(ref, previewId, enabled = true) {
    const [visibility, setVisibility] = useState({ previewId, visible: false });
    useEffect(() => {
        const element = ref.current;
        const setVisible = visible => setVisibility(current => current.previewId === previewId && current.visible === visible
            ? current : { previewId, visible });
        setVisible(false);
        if (!enabled || !element) return undefined;
        if (!subscribers.size) startObserving();
        subscribers.set(element, { intersecting: false, visible: false, setVisible });
        observer?.observe(element);
        scheduleMeasurement();
        return () => {
            observer?.unobserve(element);
            subscribers.delete(element);
            if (!subscribers.size) stopObserving();
        };
    }, [ref, previewId, enabled]);
    return enabled && visibility.previewId === previewId && visibility.visible;
}
